import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, BACKEND_URL } from "../lib/api";
import { LockedOverlay } from "../components/LockedOverlay";
import { FileDown, FileText } from "lucide-react";
import { toast } from "sonner";

export default function PdfLibrary() {
  const { user } = useAuth();
  const [pdfs, setPdfs] = useState([]);
  const isPaid = user?.subscription_status === "paid" || user?.role === "admin";

  useEffect(() => {
    api.get("/pdfs").then(({ data }) => setPdfs(data)).catch(() => {});
  }, []);

  const download = async (pdf) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/pdfs/${pdf.id}/download`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Cannot download PDF");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pdf.title}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="container-page py-10" data-testid="pdf-library-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink">PDF library</h1>
      <p className="mt-2 text-muted2">Meal plans, shopping lists, and quick-reference guides.</p>

      {pdfs.length === 0 ? (
        <div className="mt-12 text-center text-muted2 bg-cardm border border-subtle rounded-2xl p-12">No PDFs yet. Check back soon!</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {pdfs.map((p) => {
            const unlocked = p.is_free_preview || isPaid;
            return (
              <div key={p.id} className="relative bg-white border border-subtle rounded-2xl p-6 shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all" data-testid={`pdf-card-${p.id}`}>
                <span className="inline-flex w-12 h-12 rounded-xl bg-terra/10 text-terra items-center justify-center"><FileText /></span>
                <h3 className="mt-4 font-heading font-semibold text-ink">{p.title}</h3>
                <p className="mt-1 text-sm text-muted2 line-clamp-3 min-h-[3em]">{p.description}</p>
                <div className="mt-5 flex items-center justify-between">
                  {p.is_free_preview && <span className="text-xs font-semibold uppercase tracking-wider text-leaf bg-leaf/10 rounded-full px-2 py-0.5">Free</span>}
                  {unlocked ? (
                    <button onClick={() => download(p)} data-testid={`download-${p.id}`}
                      className="ml-auto inline-flex items-center gap-2 bg-moss hover:bg-moss-dark text-white rounded-full px-4 py-2 text-sm font-semibold transition-all">
                      <FileDown size={16} /> Download
                    </button>
                  ) : null}
                </div>
                {!unlocked && <LockedOverlay message="Members-only PDF" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
